// ======================================================================
// FILE:
// src/modules/finance/transactions/payment_voucher/pages/PaymentVoucherEditPage.js
//
// MODULE:
// Finance & Accounts Management System (FBAMS)
//
// FEATURE:
// Payment Voucher - Edit
//
// PURPOSE:
// Edit an existing Finance Payment Voucher.
//
// IMPORTANT:
// - Uses the existing Payment Voucher Create structure.
// - Does not create a new voucher.
// - Existing voucher ID is preserved.
// - Existing voucher number is preserved.
// - Allocation lines are loaded from the existing voucher.
// - Accounting logic remains in backend.
// ======================================================================

import React, {
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    useNavigate,
    useParams,
} from "react-router-dom";

import AsyncSelect
    from "react-select/async";

import Select
    from "react-select";

import FinancePageHeader
    from "../../../components/FinancePageHeader";

import paymentVoucherService
    from "../services/paymentVoucherService";

import financeLookupService
    from "../../../services/financeLookupService";


// ======================================================================
// OPTIONS
// ======================================================================

const PAYEE_TYPE_OPTIONS = [
    {
        value: "BUSINESS_PARTNER",
        label: "Business Partner",
    },
    {
        value: "VENDOR",
        label: "Vendor",
    },
    {
        value: "CUSTOMER",
        label: "Customer",
    },
    {
        value: "EMPLOYEE",
        label: "Employee",
    },
    {
        value: "GOVERNMENT",
        label: "Government / Tax Authority",
    },
    {
        value: "OTHER",
        label: "Other",
    },
];


const PAYMENT_MODE_OPTIONS = [
    {
        value: "BANK_TRANSFER",
        label: "Bank Transfer",
    },
    {
        value: "NEFT",
        label: "NEFT",
    },
    {
        value: "RTGS",
        label: "RTGS",
    },
    {
        value: "IMPS",
        label: "IMPS",
    },
    {
        value: "CHEQUE",
        label: "Cheque",
    },
    {
        value: "CASH",
        label: "Cash",
    },
    {
        value: "OTHER",
        label: "Other",
    },
];


const ALLOCATION_TYPE_OPTIONS = [
    {
        value: "MANUAL",
        label: "Manual",
    },
    {
        value: "INVOICE",
        label: "Invoice",
    },
    {
        value: "ADVANCE",
        label: "Advance",
    },
    {
        value: "TAX",
        label: "Tax",
    },
    {
        value: "OTHER",
        label: "Other",
    },
];


// ======================================================================
// INITIAL STATE
// ======================================================================

const getInitialForm = () => ({
    company_id: null,
    company_option: null,

    voucher_date:
        "",

    payment_account_id: null,

    payment_account_option: null,

    payee_type: "",

    payee_id: null,

    payee_option: null,

    payee_name: "",

    payment_mode: "",

    reference_no: "",

    reference_date: "",

    currency_id: null,

    currency_option: null,

    exchange_rate: "1",

    remarks: "",
});


const getInitialLine = (
    sequenceNo = 1
) => ({
    id: null,

    sequence_no:
        sequenceNo,

    account_id: null,

    account_option: null,

    allocation_type:
        "MANUAL",

    business_partner_id: null,

    vendor_id: null,

    customer_id: null,

    employee_id: null,

    transaction_amount: "",

    base_amount: "",

    cost_center_id: null,

    cost_center_option: null,

    project_id: null,

    project_option: null,

    department_id: null,

    description: "",

    source_type: "",

    source_id: null,

    source_reference: "",
});


// ======================================================================
// COMPONENT
// ======================================================================

export default function PaymentVoucherEditPage() {

    const navigate =
        useNavigate();


    const {
        id
    } = useParams();


    // ==================================================================
    // STATE
    // ==================================================================

    const [
        formData,
        setFormData
    ] = useState(
        getInitialForm()
    );


    const [
        lines,
        setLines
    ] = useState([
        getInitialLine(1)
    ]);


    const [
        loading,
        setLoading
    ] = useState(true);


    const [
        saving,
        setSaving
    ] = useState(false);


    const [
        errorMessage,
        setErrorMessage
    ] = useState("");


    const [
        voucher,
        setVoucher
    ] = useState(null);


    // ==================================================================
    // LOAD EXISTING VOUCHER
    // ==================================================================

    useEffect(
        () => {

            if (
                !id
            ) {

                setErrorMessage(
                    "Payment Voucher ID is required."
                );

                setLoading(false);

                return;

            }


            loadVoucher();

        },
        [
            id
        ]
    );


    async function loadVoucher() {

        try {

            setLoading(true);

            setErrorMessage("");


            const data =
            await paymentVoucherService
                .getPaymentVoucher(
                    id
                );


            if (
                !data
            ) {

                throw new Error(
                    "Payment Voucher not found."
                );

            }


            setVoucher(
                data
            );


            populateForm(
                data
            );

        }
        catch (
            error
        ) {

            console.error(
                "Payment Voucher load failed:",
                error
            );


            setErrorMessage(
                error?.response?.data?.message ||
                error?.response?.data?.error ||
                error?.message ||
                "Failed to load Payment Voucher."
            );

        }
        finally {

            setLoading(false);

        }

    }


// ======================================================================
// POPULATE FORM
// ======================================================================

    function populateForm(
        data
    ) {

        const companyOption =
            data.company_id
                ? {
                    value:
                        data.company_id,

                    label:
                        data.company_name ||
                        data.company_code ||
                        `Company #${data.company_id}`,

                    data,
                }
                : null;


        const paymentAccountOption =
            data.payment_account_id
                ? {
                    value:
                        data.payment_account_id,

                    id:
                        data.payment_account_id,

                    account_name:
                        data.payment_account_name ||
                        "",

                    account_number:
                        data.payment_account_number ||
                        "",

                    branch_name:
                        data.payment_account_branch_name ||
                        "",

                    label:
                        data.payment_account_name ||
                        data.payment_account_number ||
                        `Account #${data.payment_account_id}`,

                    data,
                }
                : null;


        const payeeOption =
            data.payee_id
                ? {
                    value:
                        data.payee_id,

                    label:
                        data.payee_name ||
                        `Payee #${data.payee_id}`,

                    data: {
                        id:
                            data.payee_id,

                        bp_name:
                            data.payee_name ||
                            "",
                    },
                }
                : null;


        const currencyOption =
            data.currency_id
                ? {
                    value:
                        data.currency_id,

                    label:
                        data.currency_code ||
                        data.currency_name ||
                        `Currency #${data.currency_id}`,

                    data,
                }
                : null;


        setFormData({
            company_id:
                data.company_id ||
                null,

            company_option:
                companyOption,

            voucher_date:
                data.voucher_date
                    ? String(
                        data.voucher_date
                    ).slice(
                        0,
                        10
                    )
                    : "",

            payment_account_id:
                data.payment_account_id ||
                null,

            payment_account_option:
                paymentAccountOption,

            payee_type:
                data.payee_type ||
                "",

            payee_id:
                data.payee_id ||
                null,

            payee_option:
                payeeOption,

            payee_name:
                data.payee_name ||
                "",

            payment_mode:
                data.payment_mode ||
                "",

            reference_no:
                data.reference_no ||
                "",

            reference_date:
                data.reference_date
                    ? String(
                        data.reference_date
                    ).slice(
                        0,
                        10
                    )
                    : "",

            currency_id:
                data.currency_id ||
                null,

            currency_option:
                currencyOption,

            exchange_rate:
                data.exchange_rate !==
                null &&
                data.exchange_rate !==
                undefined
                    ? String(
                        data.exchange_rate
                    )
                    : "1",

            remarks:
                data.remarks ||
                "",
        });


        const existingLines =
            Array.isArray(
                data.lines
            )
                ? data.lines
                : [];


        if (
            existingLines.length === 0
        ) {

            setLines([
                getInitialLine(1)
            ]);

            return;

        }


        setLines(
            existingLines.map(
                (
                    line,
                    index
                ) => {

                    const accountOption =
                        line.account_id
                            ? {
                                value:
                                    line.account_id,

                                id:
                                    line.account_id,

                                label:
                                    line.account_name ||
                                    line.account_code ||
                                    `Account #${line.account_id}`,

                                data:
                                    line,
                            }
                            : null;


                    const costCenterOption =
                        line.cost_center_id
                            ? {
                                value:
                                    line.cost_center_id,

                                id:
                                    line.cost_center_id,

                                label:
                                    line.cost_center_name ||
                                    line.cost_center_code ||
                                    `Cost Centre #${line.cost_center_id}`,

                                data:
                                    line,
                            }
                            : null;


                    return {

                        id:
                            line.id ||
                            null,

                        sequence_no:
                            index + 1,

                        account_id:
                            line.account_id ||
                            null,

                        account_option:
                            accountOption,

                        allocation_type:
                            line.allocation_type ||
                            "MANUAL",

                        business_partner_id:
                            line.business_partner_id ||
                            null,

                        vendor_id:
                            line.vendor_id ||
                            null,

                        customer_id:
                            line.customer_id ||
                            null,

                        employee_id:
                            line.employee_id ||
                            null,

                        transaction_amount:
                            line.transaction_amount !==
                            null &&
                            line.transaction_amount !==
                            undefined
                                ? String(
                                    line.transaction_amount
                                )
                                : "",

                        base_amount:
                            line.base_amount !==
                            null &&
                            line.base_amount !==
                            undefined
                                ? String(
                                    line.base_amount
                                )
                                : "",

                        cost_center_id:
                            line.cost_center_id ||
                            null,

                        cost_center_option:
                            costCenterOption,

                        project_id:
                            line.project_id ||
                            null,

                        project_option:
                            line.project_id
                                ? {
                                    value:
                                        line.project_id,

                                    id:
                                        line.project_id,

                                    label:
                                        line.project_name ||
                                        line.project_code ||
                                        `Project #${line.project_id}`,

                                    data:
                                        line,
                                }
                                : null,

                        department_id:
                            line.department_id ||
                            null,

                        description:
                            line.description ||
                            "",

                        source_type:
                            line.source_type ||
                            "",

                        source_id:
                            line.source_id ||
                            null,

                        source_reference:
                            line.source_reference ||
                            "",
                    };

                }
            )
        );

    }
        // ==================================================================
    // COMPANY LOOKUP
    // ==================================================================

    async function loadCompanies(
        inputValue = ""
    ) {

        try {

            const response =
                await financeLookupService
                    .searchCompanies(
                        inputValue
                    );


            return (
                response || []
            ).map(
                company => ({

                    value:
                        company.id,

                    label:
                        company.company_name,

                    data:
                        company,

                })
            );

        }
        catch (
            error
        ) {

            console.error(
                "Company lookup failed:",
                error
            );


            return [];

        }

    }


    // ==================================================================
    // PAYMENT ACCOUNT LOOKUP
    // ==================================================================

    async function loadPaymentAccounts(
        inputValue = ""
    ) {

        if (
            !formData.company_id
        ) {

            return [];

        }


        try {

            const response =
                await financeLookupService
                    .searchPaymentAccounts(
                        formData.company_id,
                        inputValue,
                        20
                    );


            return (
                response || []
            ).map(
                account => ({

                    ...account,

                    value:
                        account.id,

                    label:
                        `${account.account_name || "Unnamed Account"}`
                        + (
                            account.account_number
                                ? ` - ${account.account_number}`
                                : ""
                        )
                        + (
                            account.branch_name
                                ? ` (${account.branch_name})`
                                : ""
                        ),

                })
            );

        }
        catch (
            error
        ) {

            console.error(
                "Payment Account lookup failed:",
                error
            );


            return [];

        }

    }


    // ==================================================================
    // VENDOR LOOKUP
    // ==================================================================

    async function loadVendors(
        inputValue = ""
    ) {

        try {

            const response =
                await financeLookupService
                    .searchVendors(
                        inputValue
                    );


            return (
                response || []
            ).map(
                vendor => ({

                    value:
                        vendor.id,

                    label:
                        `${vendor.bp_code || ""} - ${vendor.bp_name || ""}`
                            .replace(
                                /^ - /,
                                ""
                            ),

                    data:
                        vendor,

                })
            );

        }
        catch (
            error
        ) {

            console.error(
                "Vendor lookup failed:",
                error
            );


            return [];

        }

    }


    // ==================================================================
    // CUSTOMER LOOKUP
    // ==================================================================

    async function loadCustomers(
        inputValue = ""
    ) {

        try {

            const response =
                await financeLookupService
                    .searchCustomers(
                        inputValue
                    );


            return (
                response || []
            ).map(
                customer => ({

                    value:
                        customer.id,

                    label:
                        `${customer.bp_code || ""} - ${customer.bp_name || ""}`
                            .replace(
                                /^ - /,
                                ""
                            ),

                    data:
                        customer,

                })
            );

        }
        catch (
            error
        ) {

            console.error(
                "Customer lookup failed:",
                error
            );


            return [];

        }

    }


    // ==================================================================
    // CURRENCY LOOKUP
    // ==================================================================

    async function loadCurrencies(
        inputValue = ""
    ) {

        try {

            const response =
                await financeLookupService
                    .searchCurrencies(
                        inputValue
                    );


            return (
                response || []
            ).map(
                currency => ({

                    value:
                        currency.id ||
                        currency.value,

                    label:
                        currency.currency_name ||
                        currency.currency_code ||
                        currency.label,

                    data:
                        currency,

                })
            );

        }
        catch (
            error
        ) {

            console.error(
                "Currency lookup failed:",
                error
            );


            return [];

        }

    }


    // ==================================================================
    // ACCOUNT LOOKUP
    // ==================================================================

    async function loadAccounts(
        inputValue = ""
    ) {

        if (
            !formData.company_id
        ) {

            return [];

        }


        try {

            const response =
                await financeLookupService
                    .searchChartOfAccounts(
                        formData.company_id,
                        inputValue,
                        null,
                        20
                    );


            return response || [];

        }
        catch (
            error
        ) {

            console.error(
                "Account lookup failed:",
                error
            );


            return [];

        }

    }


    // ==================================================================
    // COST CENTRE LOOKUP
    // ==================================================================

    async function loadCostCenters(
        inputValue = ""
    ) {

        if (
            !formData.company_id
        ) {

            return [];

        }


        try {

            const response =
                await financeLookupService
                    .searchCostCenters(
                        formData.company_id,
                        inputValue,
                        20
                    );


            return response || [];

        }
        catch (
            error
        ) {

            console.error(
                "Cost centre lookup failed:",
                error
            );


            return [];

        }

    }


    // ==================================================================
    // HEADER CHANGE
    // ==================================================================

    function handleFieldChange(
        field,
        value,
        option = null
    ) {

        setFormData(
            previous => {

                const updated = {

                    ...previous,

                    [field]:
                        value,

                };


                // ------------------------------------------------------
                // PAYEE TYPE
                // ------------------------------------------------------

                if (
                    field ===
                    "payee_type"
                ) {

                    updated.payee_id =
                        null;

                    updated.payee_option =
                        null;

                    updated.payee_name =
                        "";

                }


                // ------------------------------------------------------
                // PAYEE
                // ------------------------------------------------------

                if (
                    field ===
                    "payee_id"
                ) {

                    updated.payee_option =
                        option;

                    updated.payee_name =
                        option?.data?.bp_name ||
                        option?.label ||
                        "";

                }


                // ------------------------------------------------------
                // COMPANY
                // ------------------------------------------------------

                if (
                    field ===
                    "company_id"
                ) {

                    updated.company_option =
                        option;


                    // Payment Account belongs
                    // to Company.

                    updated.payment_account_id =
                        null;

                    updated.payment_account_option =
                        null;


                    // Existing voucher should never
                    // silently retain an account from
                    // another company.

                    setLines(
                        previousLines =>
                            previousLines.map(
                                line => ({
                                    ...line,

                                    account_id:
                                        null,

                                    account_option:
                                        null,

                                    cost_center_id:
                                        null,

                                    cost_center_option:
                                        null,

                                })
                            )
                    );

                }


                // ------------------------------------------------------
                // PAYMENT ACCOUNT
                // ------------------------------------------------------

                if (
                    field ===
                    "payment_account_id"
                ) {

                    updated.payment_account_option =
                        option;

                }


                // ------------------------------------------------------
                // CURRENCY
                // ------------------------------------------------------

                if (
                    field ===
                    "currency_id"
                ) {

                    updated.currency_option =
                        option;

                }


                return updated;

            }
        );


        setErrorMessage("");

    }


    // ==================================================================
    // LINE CHANGE
    // ==================================================================

    function handleLineChange(
        index,
        field,
        value,
        option = null
    ) {

        setLines(
            previous => {

                const updated =
                    [
                        ...previous
                    ];


                updated[index] = {

                    ...updated[index],

                    [field]:
                        value,

                };


                // ------------------------------------------------------
                // ACCOUNT
                // ------------------------------------------------------

                if (
                    field ===
                    "account_id"
                ) {

                    updated[index]
                        .account_option =
                            option;

                }


                // ------------------------------------------------------
                // COST CENTRE
                // ------------------------------------------------------

                if (
                    field ===
                    "cost_center_id"
                ) {

                    updated[index]
                        .cost_center_option =
                            option;

                }


                // ------------------------------------------------------
                // PROJECT
                // ------------------------------------------------------

                if (
                    field ===
                    "project_id"
                ) {

                    updated[index]
                        .project_option =
                            option;

                }


                return updated;

            }
        );


        setErrorMessage("");

    }


    // ==================================================================
    // ADD LINE
    // ==================================================================

    function addLine() {

        setLines(
            previous => [

                ...previous,

                getInitialLine(
                    previous.length + 1
                ),

            ]
        );

    }


    // ==================================================================
    // REMOVE LINE
    // ==================================================================

    function removeLine(
        index
    ) {

        if (
            lines.length <= 1
        ) {

            return;

        }


        setLines(
            previous => {

                const updated =
                    previous.filter(
                        (
                            _line,
                            lineIndex
                        ) =>
                            lineIndex !==
                            index
                    );


                return updated.map(
                    (
                        line,
                        lineIndex
                    ) => ({

                        ...line,

                        sequence_no:
                            lineIndex + 1,

                    })
                );

            }
        );

    }


    // ==================================================================
    // TOTALS
    // ==================================================================

    const totalAmount =
        useMemo(
            () =>
                lines.reduce(
                    (
                        total,
                        line
                    ) =>
                        total +
                        (
                            Number(
                                line.transaction_amount
                            ) || 0
                        ),
                    0
                ),
            [
                lines
            ]
        );


    const totalBaseAmount =
        useMemo(
            () =>
                lines.reduce(
                    (
                        total,
                        line
                    ) =>
                        total +
                        (
                            Number(
                                line.base_amount
                            ) ||
                            (
                                Number(
                                    line.transaction_amount
                                ) *
                                (
                                    Number(
                                        formData.exchange_rate
                                    ) || 1
                                )
                            ) ||
                            0
                        ),
                    0
                ),
            [
                lines,
                formData.exchange_rate,
            ]
        );


    // ==================================================================
    // VALIDATION
    // ==================================================================

    function validateForm() {

        const errors = [];


        if (
            !formData.company_id
        ) {

            errors.push(
                "Company is required."
            );

        }


        if (
            !formData.voucher_date
        ) {

            errors.push(
                "Voucher Date is required."
            );

        }


        if (
            !formData.payment_account_id
        ) {

            errors.push(
                "Payment Account is required."
            );

        }


        if (
            !formData.payee_type
        ) {

            errors.push(
                "Payee Type is required."
            );

        }


        if (
            !formData.payment_mode
        ) {

            errors.push(
                "Payment Mode is required."
            );

        }


        if (
            !formData.currency_id
        ) {

            errors.push(
                "Currency is required."
            );

        }


        if (
            Number(
                formData.exchange_rate
            ) <= 0
        ) {

            errors.push(
                "Exchange Rate must be greater than zero."
            );

        }


        if (
            !lines.length
        ) {

            errors.push(
                "At least one allocation line is required."
            );

        }


        lines.forEach(
            (
                line,
                index
            ) => {

                if (
                    !line.account_id
                ) {

                    errors.push(
                        `GL Account is required for line ${index + 1}.`
                    );

                }


                if (
                    Number(
                        line.transaction_amount
                    ) <= 0
                ) {

                    errors.push(
                        `Amount is required for line ${index + 1}.`
                    );

                }

            }
        );


        return errors;

    }


    // ==================================================================
    // BUILD UPDATE PAYLOAD
    // ==================================================================

    function buildPayload() {

        const exchangeRate =
            Number(
                formData.exchange_rate
            ) || 1;


        return {

            company_id:
                Number(
                    formData.company_id
                ),

            voucher_date:
                formData.voucher_date,

            payment_account_id:
                Number(
                    formData.payment_account_id
                ),

            payee_type:
                formData.payee_type,

            payee_id:
                formData.payee_id
                    ? Number(
                        formData.payee_id
                    )
                    : null,

            payee_name:
                formData.payee_name
                    ?.trim() ||
                null,

            payment_mode:
                formData.payment_mode,

            reference_no:
                formData.reference_no
                    ?.trim() ||
                null,

            reference_date:
                formData.reference_date ||
                null,

            currency_id:
                Number(
                    formData.currency_id
                ),

            exchange_rate:
                exchangeRate,

            remarks:
                formData.remarks
                    ?.trim() ||
                null,

            lines:
                lines.map(
                    (
                        line,
                        index
                    ) => {

                        const amount =
                            Number(
                                line.transaction_amount
                            ) || 0;


                        return {

                            id:
                                line.id ||
                                null,

                            sequence_no:
                                index + 1,

                            account_id:
                                Number(
                                    line.account_id
                                ),

                            allocation_type:
                                line.allocation_type ||
                                "MANUAL",

                            business_partner_id:
                                line.business_partner_id
                                    ? Number(
                                        line.business_partner_id
                                    )
                                    : null,

                            vendor_id:
                                line.vendor_id
                                    ? Number(
                                        line.vendor_id
                                    )
                                    : null,

                            customer_id:
                                line.customer_id
                                    ? Number(
                                        line.customer_id
                                    )
                                    : null,

                            employee_id:
                                line.employee_id
                                    ? Number(
                                        line.employee_id
                                    )
                                    : null,

                            transaction_amount:
                                amount,

                            base_amount:
                                Number(
                                    line.base_amount
                                ) ||
                                (
                                    amount *
                                    exchangeRate
                                ),

                            cost_center_id:
                                line.cost_center_id
                                    ? Number(
                                        line.cost_center_id
                                    )
                                    : null,

                            project_id:
                                line.project_id
                                    ? Number(
                                        line.project_id
                                    )
                                    : null,

                            department_id:
                                line.department_id
                                    ? Number(
                                        line.department_id
                                    )
                                    : null,

                            description:
                                line.description
                                    ?.trim() ||
                                null,

                            source_type:
                                line.source_type
                                    ?.trim() ||
                                null,

                            source_id:
                                line.source_id
                                    ? Number(
                                        line.source_id
                                    )
                                    : null,

                            source_reference:
                                line.source_reference
                                    ?.trim() ||
                                null,

                        };

                    }
                ),

        };

    }
        // ==================================================================
    // UPDATE DRAFT
    // ==================================================================

    async function handleUpdateDraft() {

        const errors =
            validateForm();


        if (
            errors.length
        ) {

            setErrorMessage(
                errors.join(" ")
            );

            return;

        }


        // --------------------------------------------------------------
        // Backend is authoritative, but prevent obvious UI mistake.
        // Only Draft vouchers should reach the update API.
        // --------------------------------------------------------------

        if (
            voucher &&
            voucher.status &&
            String(
                voucher.status
            ).toUpperCase() !==
                "DRAFT"
        ) {

            setErrorMessage(
                "Only Draft Payment Vouchers can be edited."
            );

            return;

        }


        try {

            setSaving(
                true
            );

            setErrorMessage("");


            const payload =
                buildPayload();


            const response =
                await paymentVoucherService
                    .updatePaymentVoucher(
                        id,
                        payload
                    );


            const updatedVoucher =
                response?.data ||
                response;


            navigate(
                `/finance/payment-vouchers/view/${id}`
            );

        }
        catch (
            error
        ) {

            console.error(
                "Payment Voucher update failed:",
                error
            );


            setErrorMessage(
                error?.response?.data?.message ||
                error?.response?.data?.error ||
                error?.message ||
                "Failed to update Payment Voucher."
            );

        }
        finally {

            setSaving(
                false
            );

        }

    }


    // ==================================================================
    // CANCEL
    // ==================================================================

    function handleCancel() {

        navigate(
            `/finance/payment-vouchers/view/${id}`
        );

    }


    // ==================================================================
    // FORMAT
    // ==================================================================

    function formatAmount(
        value
    ) {

        return Number(
            value || 0
        ).toLocaleString(
            "en-IN",
            {
                minimumFractionDigits:
                    2,

                maximumFractionDigits:
                    2,
            }
        );

    }
        // ==================================================================
    // LOADING
    // ==================================================================

    if (
        loading
    ) {

        return (
            <div
                className="
                    d-flex
                    justify-content-center
                    align-items-center
                    py-5
                "
            >

                <div
                    className="
                        text-muted
                    "
                >

                    <i
                        className="
                            fas
                            fa-spinner
                            fa-spin
                            me-2
                        "
                    />

                    Loading Payment Voucher...

                </div>

            </div>
        );

    }
        // ==================================================================
    // ERROR / NOT FOUND
    // ==================================================================

    if (
        !voucher
    ) {

        return (
            <div
                className="container-fluid"
            >

                <div
                    className="
                        alert
                        alert-danger
                        mt-4
                    "
                >

                    <i
                        className="
                            fas
                            fa-exclamation-triangle
                            me-2
                        "
                    />

                    {
                        errorMessage ||
                        "Payment Voucher not found."
                    }

                </div>


                <div
                    className="mt-3"
                >

                    <button
                        type="button"
                        className="
                            btn
                            btn-outline-secondary
                        "
                        onClick={
                            handleCancel
                        }
                    >

                        <i
                            className="
                                fas
                                fa-arrow-left
                                me-1
                            "
                        />

                        Back to Voucher

                    </button>

                </div>

            </div>
        );

    }
    return (

        <>

            {/* ==========================================================
                PAGE HEADER
            ========================================================== */}

            <FinancePageHeader

                icon="fas fa-money-check-alt"

                title="Edit Payment Voucher"

                subtitle={
                    voucher.voucher_no
                        ? `Edit Payment Voucher ${voucher.voucher_no}`
                        : "Edit a Finance Payment Voucher."
                }

                breadcrumbs={[

                    {
                        label:
                            "Finance",

                        to:
                            "/finance",
                    },

                    {
                        label:
                            "Payment Voucher",

                        to:
                            "/finance/payment-vouchers",
                    },

                    {
                        label:
                            voucher.voucher_no ||
                            "Edit",
                    },

                ]}

                actions={

                    <div
                        className="
                            d-flex
                            gap-2
                        "
                    >

                        <button

                            type="button"

                            className="
                                btn
                                btn-outline-secondary
                            "

                            onClick={
                                handleCancel
                            }

                            disabled={
                                saving
                            }

                        >

                            <i
                                className="
                                    fas
                                    fa-arrow-left
                                    me-1
                                "
                            />

                            Back to Voucher

                        </button>


                        <button

                            type="button"

                            className="
                                btn
                                btn-primary
                            "

                            onClick={
                                handleUpdateDraft
                            }

                            disabled={
                                saving ||
                                (
                                    voucher.status &&
                                    String(
                                        voucher.status
                                    ).toUpperCase() !==
                                        "DRAFT"
                                )
                            }

                        >

                            {
                                saving ? (

                                    <>

                                        <span
                                            className="
                                                spinner-border
                                                spinner-border-sm
                                                me-2
                                            "
                                        />

                                        Updating...

                                    </>

                                ) : (

                                    <>

                                        <i
                                            className="
                                                fas
                                                fa-save
                                                me-1
                                            "
                                        />

                                        Update Draft

                                    </>

                                )
                            }

                        </button>

                    </div>

                }

            />
                        {/* ==========================================================
                ERROR
            ========================================================== */}

            {
                errorMessage && (

                    <div
                        className="
                            alert
                            alert-danger
                            mt-3
                        "
                    >

                        <i
                            className="
                                fas
                                fa-exclamation-triangle
                                me-2
                            "
                        />

                        {errorMessage}

                    </div>

                )
            }
                        {/* ==========================================================
                PAYMENT VOUCHER DETAILS
            ========================================================== */}

            <div
                className="
                    card
                    shadow-sm
                    border-0
                    mt-3
                "
            >

                <div
                    className="card-header"
                >

                    <h5
                        className="mb-0"
                    >

                        <i
                            className="
                                fas
                                fa-file-invoice-dollar
                                me-2
                            "
                        />

                        Payment Voucher Details

                    </h5>

                </div>


                <div
                    className="card-body"
                >

                    <div
                        className="row g-3"
                    >

                        {/* ==================================================
                            COMPANY
                        ================================================== */}

                        <div
                            className="
                                col-lg-4
                                col-md-6
                            "
                        >

                            <label
                                className="form-label"
                            >

                                Company

                                <span
                                    className="text-danger"
                                >
                                    *
                                </span>

                            </label>


                            <AsyncSelect

                                cacheOptions

                                defaultOptions

                                loadOptions={
                                    loadCompanies
                                }

                                isClearable={false}

                                isDisabled={
                                    true
                                }

                                value={
                                    formData.company_option
                                }

                                placeholder="
                                    Company
                                "

                            />

                        </div>


                        {/* ==================================================
                            VOUCHER DATE
                        ================================================== */}

                        <div
                            className="
                                col-lg-4
                                col-md-6
                            "
                        >

                            <label
                                className="form-label"
                            >

                                Voucher Date

                                <span
                                    className="text-danger"
                                >
                                    *
                                </span>

                            </label>


                            <input

                                type="date"

                                className="form-control"

                                value={
                                    formData.voucher_date
                                }

                                onChange={
                                    event =>
                                        handleFieldChange(
                                            "voucher_date",
                                            event.target.value
                                        )
                                }

                                disabled={
                                    saving
                                }

                            />

                        </div>


                        {/* ==================================================
                            PAYMENT ACCOUNT
                        ================================================== */}

                        <div
                            className="
                                col-lg-4
                                col-md-6
                            "
                        >

                            <label
                                className="form-label"
                            >

                                Payment Account

                                <span
                                    className="text-danger"
                                >
                                    *
                                </span>

                            </label>


                            <AsyncSelect

                                key={
                                    formData.company_id ||
                                    "payment-account"
                                }

                                cacheOptions

                                defaultOptions

                                loadOptions={
                                    loadPaymentAccounts
                                }

                                isClearable

                                isDisabled={
                                    saving ||
                                    !formData.company_id
                                }

                                value={
                                    formData.payment_account_option
                                }

                                onChange={
                                    option => {

                                        handleFieldChange(
                                            "payment_account_id",
                                            option?.value ||
                                            null,
                                            option
                                        );

                                    }
                                }

                                placeholder={
                                    formData.company_id
                                        ? "Select Payment Account"
                                        : "Select Company First"
                                }

                            />

                        </div>

                    </div>

                </div>

            </div>
                        {/* ==========================================================
                PAYEE / PAYMENT DETAILS
            ========================================================== */}

            <div
                className="
                    card
                    shadow-sm
                    border-0
                    mt-3
                "
            >

                <div
                    className="card-body"
                >

                    <div
                        className="row g-3"
                    >

                        {/* ==================================================
                            PAYEE TYPE
                        ================================================== */}

                        <div
                            className="
                                col-lg-4
                                col-md-6
                            "
                        >

                            <label
                                className="form-label"
                            >

                                Payee Type

                                <span
                                    className="text-danger"
                                >
                                    *
                                </span>

                            </label>


                            <Select

                                options={
                                    PAYEE_TYPE_OPTIONS
                                }

                                isClearable

                                value={
                                    PAYEE_TYPE_OPTIONS.find(
                                        option =>
                                            option.value ===
                                            formData.payee_type
                                    ) ||
                                    null
                                }

                                onChange={
                                    option =>
                                        handleFieldChange(
                                            "payee_type",
                                            option?.value ||
                                            ""
                                        )
                                }

                                isDisabled={
                                    saving
                                }

                                placeholder="
                                    Select Payee Type
                                "

                            />

                        </div>


                        {/* ==================================================
                            PAYEE
                        ================================================== */}

                        <div
                            className="
                                col-lg-4
                                col-md-6
                            "
                        >

                            <label
                                className="form-label"
                            >
                                Payee
                            </label>


                            {
                                formData.payee_type ===
                                "VENDOR" ? (

                                    <AsyncSelect

                                        cacheOptions

                                        defaultOptions

                                        loadOptions={
                                            loadVendors
                                        }

                                        isClearable

                                        value={
                                            formData.payee_option
                                        }

                                        onChange={
                                            option =>
                                                handleFieldChange(
                                                    "payee_id",
                                                    option?.value ||
                                                    null,
                                                    option
                                                )
                                        }

                                        isDisabled={
                                            saving
                                        }

                                        placeholder="
                                            Select Vendor
                                        "

                                    />

                                ) : formData.payee_type ===
                                "CUSTOMER" ? (

                                    <AsyncSelect

                                        cacheOptions

                                        defaultOptions

                                        loadOptions={
                                            loadCustomers
                                        }

                                        isClearable

                                        value={
                                            formData.payee_option
                                        }

                                        onChange={
                                            option =>
                                                handleFieldChange(
                                                    "payee_id",
                                                    option?.value ||
                                                    null,
                                                    option
                                                )
                                        }

                                        isDisabled={
                                            saving
                                        }

                                        placeholder="
                                            Select Customer
                                        "

                                    />

                                ) : (

                                    <input

                                        type="text"

                                        className="form-control"

                                        value={
                                            formData.payee_name
                                        }

                                        onChange={
                                            event =>
                                                handleFieldChange(
                                                    "payee_name",
                                                    event.target.value
                                                )
                                        }

                                        disabled={
                                            saving
                                        }

                                        placeholder="
                                            Enter payee name
                                        "

                                    />

                                )
                            }

                        </div>


                        {/* ==================================================
                            PAYMENT MODE
                        ================================================== */}

                        <div
                            className="
                                col-lg-4
                                col-md-6
                            "
                        >

                            <label
                                className="form-label"
                            >

                                Payment Mode

                                <span
                                    className="text-danger"
                                >
                                    *
                                </span>

                            </label>


                            <Select

                                options={
                                    PAYMENT_MODE_OPTIONS
                                }

                                isClearable

                                value={
                                    PAYMENT_MODE_OPTIONS.find(
                                        option =>
                                            option.value ===
                                            formData.payment_mode
                                    ) ||
                                    null
                                }

                                onChange={
                                    option =>
                                        handleFieldChange(
                                            "payment_mode",
                                            option?.value ||
                                            ""
                                        )
                                }

                                isDisabled={
                                    saving
                                }

                                placeholder="
                                    Select Payment Mode
                                "

                            />

                        </div>

                    </div>

                </div>

            </div>
                        {/* ==========================================================
                REFERENCE / CURRENCY DETAILS
            ========================================================== */}

            <div
                className="
                    card
                    shadow-sm
                    border-0
                    mt-3
                "
            >

                <div
                    className="card-body"
                >

                    <div
                        className="row g-3"
                    >

                        {/* ==================================================
                            REFERENCE NO
                        ================================================== */}

                        <div
                            className="
                                col-lg-4
                                col-md-6
                            "
                        >

                            <label
                                className="form-label"
                            >
                                Reference No
                            </label>

                            <input

                                type="text"

                                className="form-control"

                                value={
                                    formData.reference_no
                                }

                                onChange={
                                    event =>
                                        handleFieldChange(
                                            "reference_no",
                                            event.target.value
                                        )
                                }

                                disabled={
                                    saving
                                }

                                placeholder="
                                    Cheque / UTR / Transaction No
                                "

                            />

                        </div>


                        {/* ==================================================
                            REFERENCE DATE
                        ================================================== */}

                        <div
                            className="
                                col-lg-4
                                col-md-6
                            "
                        >

                            <label
                                className="form-label"
                            >
                                Reference Date
                            </label>

                            <input

                                type="date"

                                className="form-control"

                                value={
                                    formData.reference_date
                                }

                                onChange={
                                    event =>
                                        handleFieldChange(
                                            "reference_date",
                                            event.target.value
                                        )
                                }

                                disabled={
                                    saving
                                }

                            />

                        </div>


                        {/* ==================================================
                            CURRENCY
                        ================================================== */}

                        <div
                            className="
                                col-lg-4
                                col-md-6
                            "
                        >

                            <label
                                className="form-label"
                            >

                                Currency

                                <span
                                    className="text-danger"
                                >
                                    *
                                </span>

                            </label>

                            <AsyncSelect

                                cacheOptions

                                defaultOptions

                                loadOptions={
                                    loadCurrencies
                                }

                                isClearable

                                value={
                                    formData.currency_option
                                }

                                onChange={
                                    option =>
                                        handleFieldChange(
                                            "currency_id",
                                            option?.value ||
                                            null,
                                            option
                                        )
                                }

                                isDisabled={
                                    saving
                                }

                                placeholder="
                                    Select Currency
                                "

                            />

                        </div>


                        {/* ==================================================
                            EXCHANGE RATE
                        ================================================== */}

                        <div
                            className="
                                col-lg-4
                                col-md-6
                            "
                        >

                            <label
                                className="form-label"
                            >

                                Exchange Rate

                                <span
                                    className="text-danger"
                                >
                                    *
                                </span>

                            </label>

                            <input

                                type="number"

                                step="0.00000001"

                                min="0"

                                className="form-control"

                                value={
                                    formData.exchange_rate
                                }

                                onChange={
                                    event =>
                                        handleFieldChange(
                                            "exchange_rate",
                                            event.target.value
                                        )
                                }

                                disabled={
                                    saving
                                }

                            />

                        </div>


                        {/* ==================================================
                            REMARKS
                        ================================================== */}

                        <div
                            className="col-12"
                        >

                            <label
                                className="form-label"
                            >
                                Remarks
                            </label>

                            <textarea

                                className="form-control"

                                rows={3}

                                value={
                                    formData.remarks
                                }

                                onChange={
                                    event =>
                                        handleFieldChange(
                                            "remarks",
                                            event.target.value
                                        )
                                }

                                disabled={
                                    saving
                                }

                                placeholder="
                                    Enter remarks
                                "

                            />

                        </div>

                    </div>

                </div>

            </div>
                        {/* ==========================================================
                ALLOCATION DETAILS
            ========================================================== */}

            <div
                className="
                    card
                    shadow-sm
                    border-0
                    mt-3
                "
            >

                <div
                    className="
                        card-header
                        d-flex
                        justify-content-between
                        align-items-center
                    "
                >

                    <h5
                        className="mb-0"
                    >

                        <i
                            className="
                                fas
                                fa-list
                                me-2
                            "
                        />

                        Payment Allocation Details

                    </h5>


                    <button

                        type="button"

                        className="
                            btn
                            btn-sm
                            btn-outline-primary
                        "

                        onClick={
                            addLine
                        }

                        disabled={
                            saving
                        }

                    >

                        <i
                            className="
                                fas
                                fa-plus
                                me-1
                            "
                        />

                        Add Line

                    </button>

                </div>


                <div
                    className="card-body p-0"
                >

                    <div
                        className="table-responsive"
                    >

                        <table
                            className="
                                table
                                table-bordered
                                align-middle
                                mb-0
                            "
                        >

                            <thead
                                className="table-light"
                            >

                                <tr>

                                    <th
                                        style={{
                                            width: 55,
                                        }}
                                    >
                                        #
                                    </th>

                                    <th>
                                        GL Account
                                    </th>

                                    <th>
                                        Allocation Type
                                    </th>

                                    <th>
                                        Transaction Amount
                                    </th>

                                    <th>
                                        Base Amount
                                    </th>

                                    <th>
                                        Description
                                    </th>

                                    <th
                                        style={{
                                            width: 70,
                                        }}
                                    />

                                </tr>

                            </thead>


                            <tbody>

                                {
                                    lines.map(
                                        (
                                            line,
                                            index
                                        ) => (

                                            <tr
                                                key={
                                                    line.id ||
                                                    `new-${index}`
                                                }
                                            >

                                                {/* ==================================
                                                    SEQUENCE
                                                ================================== */}

                                                <td>

                                                    {
                                                        index + 1
                                                    }

                                                </td>


                                                {/* ==================================
                                                    GL ACCOUNT
                                                ================================== */}

                                                <td
                                                    style={{
                                                        minWidth: 280,
                                                    }}
                                                >

                                                    <AsyncSelect

                                                        cacheOptions

                                                        defaultOptions

                                                        loadOptions={
                                                            loadAccounts
                                                        }

                                                        isClearable

                                                        value={
                                                            line.account_option
                                                        }

                                                        onChange={
                                                            option =>
                                                                handleLineChange(
                                                                    index,
                                                                    "account_id",
                                                                    option?.value ||
                                                                    null,
                                                                    option
                                                                )
                                                        }

                                                        isDisabled={
                                                            saving
                                                        }

                                                        placeholder="
                                                            Select Account
                                                        "

                                                    />

                                                </td>


                                                {/* ==================================
                                                    ALLOCATION TYPE
                                                ================================== */}

                                                <td
                                                    style={{
                                                        minWidth: 160,
                                                    }}
                                                >

                                                    <Select

                                                        options={
                                                            ALLOCATION_TYPE_OPTIONS
                                                        }

                                                        value={
                                                            ALLOCATION_TYPE_OPTIONS.find(
                                                                option =>
                                                                    option.value ===
                                                                    line.allocation_type
                                                            ) ||
                                                            null
                                                        }

                                                        onChange={
                                                            option =>
                                                                handleLineChange(
                                                                    index,
                                                                    "allocation_type",
                                                                    option?.value ||
                                                                    "MANUAL"
                                                                )
                                                        }

                                                        isDisabled={
                                                            saving
                                                        }

                                                    />

                                                </td>


                                                {/* ==================================
                                                    TRANSACTION AMOUNT
                                                ================================== */}

                                                <td
                                                    style={{
                                                        minWidth: 150,
                                                    }}
                                                >

                                                    <input

                                                        type="number"

                                                        min="0"

                                                        step="0.01"

                                                        className="
                                                            form-control
                                                            text-end
                                                        "

                                                        value={
                                                            line.transaction_amount
                                                        }

                                                        onChange={
                                                            event =>
                                                                handleLineChange(
                                                                    index,
                                                                    "transaction_amount",
                                                                    event.target.value
                                                                )
                                                        }

                                                        disabled={
                                                            saving
                                                        }

                                                    />

                                                </td>


                                                {/* ==================================
                                                    BASE AMOUNT
                                                ================================== */}

                                                <td
                                                    style={{
                                                        minWidth: 150,
                                                    }}
                                                >

                                                    <input

                                                        type="number"

                                                        min="0"

                                                        step="0.01"

                                                        className="
                                                            form-control
                                                            text-end
                                                        "

                                                        value={
                                                            line.base_amount
                                                        }

                                                        onChange={
                                                            event =>
                                                                handleLineChange(
                                                                    index,
                                                                    "base_amount",
                                                                    event.target.value
                                                                )
                                                        }

                                                        disabled={
                                                            saving
                                                        }

                                                        placeholder={
                                                            (
                                                                Number(
                                                                    line.transaction_amount
                                                                ) *
                                                                (
                                                                    Number(
                                                                        formData.exchange_rate
                                                                    ) || 1
                                                                )
                                                            ).toFixed(2)
                                                        }

                                                    />

                                                </td>


                                                {/* ==================================
                                                    DESCRIPTION
                                                ================================== */}

                                                <td
                                                    style={{
                                                        minWidth: 220,
                                                    }}
                                                >

                                                    <input

                                                        type="text"

                                                        className="form-control"

                                                        value={
                                                            line.description
                                                        }

                                                        onChange={
                                                            event =>
                                                                handleLineChange(
                                                                    index,
                                                                    "description",
                                                                    event.target.value
                                                                )
                                                        }

                                                        disabled={
                                                            saving
                                                        }

                                                        placeholder="
                                                            Description
                                                        "

                                                    />

                                                </td>


                                                {/* ==================================
                                                    REMOVE
                                                ================================== */}

                                                <td
                                                    className="text-center"
                                                >

                                                    <button

                                                        type="button"

                                                        className="
                                                            btn
                                                            btn-sm
                                                            btn-outline-danger
                                                        "

                                                        onClick={() =>
                                                            removeLine(
                                                                index
                                                            )
                                                        }

                                                        disabled={
                                                            saving ||
                                                            lines.length <= 1
                                                        }

                                                        title="
                                                            Remove line
                                                        "

                                                    >

                                                        <i
                                                            className="
                                                                fas
                                                                fa-trash
                                                            "
                                                        />

                                                    </button>

                                                </td>

                                            </tr>

                                        )
                                    )
                                }

                            </tbody>


                            {/* ======================================================
                                TOTALS
                            ====================================================== */}

                            <tfoot>

                                <tr
                                    className="table-light"
                                >

                                    <td
                                        colSpan={3}
                                        className="
                                            text-end
                                            fw-bold
                                        "
                                    >

                                        Total

                                    </td>


                                    <td
                                        className="
                                            text-end
                                            fw-bold
                                        "
                                    >

                                        {
                                            formatAmount(
                                                totalAmount
                                            )
                                        }

                                    </td>


                                    <td
                                        className="
                                            text-end
                                            fw-bold
                                        "
                                    >

                                        {
                                            formatAmount(
                                                totalBaseAmount
                                            )
                                        }

                                    </td>


                                    <td
                                        colSpan={2}
                                    />

                                </tr>

                            </tfoot>

                        </table>

                    </div>

                </div>

            </div>
                        {/* ==========================================================
                FOOTER ACTIONS
            ========================================================== */}

            <div
                className="
                    d-flex
                    justify-content-end
                    gap-2
                    mt-4
                    mb-4
                "
            >

                <button

                    type="button"

                    className="
                        btn
                        btn-outline-secondary
                    "

                    onClick={
                        handleCancel
                    }

                    disabled={
                        saving
                    }

                >

                    <i
                        className="
                            fas
                            fa-arrow-left
                            me-1
                        "
                    />

                    Cancel

                </button>


                <button

                    type="button"

                    className="
                        btn
                        btn-primary
                    "

                    onClick={
                        handleUpdateDraft
                    }

                    disabled={
                        saving ||
                        (
                            voucher.status &&
                            String(
                                voucher.status
                            ).toUpperCase() !==
                                "DRAFT"
                        )
                    }

                >

                    {
                        saving ? (

                            <>

                                <span
                                    className="
                                        spinner-border
                                        spinner-border-sm
                                        me-2
                                    "
                                />

                                Updating...

                            </>

                        ) : (

                            <>

                                <i
                                    className="
                                        fas
                                        fa-save
                                        me-1
                                    "
                                />

                                Update Draft

                            </>

                        )
                    }

                </button>

            </div>

        </>

    );

}
